import {addDays, fmt, toISO} from '../lib/dateUtils.js';
import {broilerDayOneCountMissing, calcPoultryStatus, calcTimeline, resolveBrooderEntryDate} from '../lib/broiler.js';
import {activePigFeederDailyTargets, buildCycleSeqMap, calcBreedingTimeline, cycleLabel} from '../lib/pig.js';
import {computeIntervalStatus, daysSince, latestSaneReading, WARRANTY_WINDOW_DAYS} from '../lib/equipment.js';
import {ANIMAL_ICON_KEYS, PLANNER_ICON_KEYS} from '../lib/plannerIcons.js';
import type {DateInput} from '../lib/dateUtils.js';
import type {BroilerBatch} from '../lib/broiler.js';
import type {PigBreederRow, PigBreedingCycle, PigFarrowingRecord, PigFeederGroup} from '../lib/pig.js';
import type {IntervalCompletion} from '../lib/equipment.js';
import {filterActiveLayerGroups} from '../lib/layerGroupStatus.js';
import {
  SOW_GROUP_NUMBERS,
  isSowsDailyLabel,
  sowGroupLabel,
  sowGroupOf,
  type SowGroupNumber,
} from '../lib/pigSowGroups.js';

// Row shapes the alert builders actually read, in the converted libs' convention
// (pig.ts/broiler.ts): name ONLY the touched fields, keep the rest addressable via
// an `unknown` index signature, and coerce numerics with Number(...) at the use
// site (equipment.ts's documented idiom). A few fields are declared non-nullable
// where bodies read them unguarded (processingDate >= todayStr, parseFloat on
// week4Lbs/week6Lbs, r.sow.trim(), Record keys, localeCompare on equipment name);
// the bodies already tolerated absent values at runtime (comparison false / NaN),
// so the stronger field is a type-level claim, not a new runtime requirement.

type HomeBroilerBatch = BroilerBatch & {
  brooderIn?: string | null;
  processingDate: string;
  week4Lbs: string;
  week6Lbs: string;
};

type HomeFarrowingRecord = PigFarrowingRecord & {sow: string};

type DailyReportRow = {
  date?: string | null;
  batch_label?: string | null;
  herd?: string | null;
  flock?: string | null;
  [column: string]: unknown;
};

type LayerGroupRow = {
  id?: string | number | null;
  name?: string | null;
  status?: string | null;
  [column: string]: unknown;
};

type ServiceCompletionRow = {
  reading_at_completion?: number | string | null;
  team_member?: string | null;
  [column: string]: unknown;
};

type FillupTick = {
  id?: string | number | null;
  [column: string]: unknown;
};

type FillupItem = {
  id?: string | number | null;
  label?: string | null;
  [column: string]: unknown;
};

export type EquipmentFuelingRow = {
  equipment_id: string | number; // Record key in foldEquipmentFuelings
  date?: string | null;
  hours_reading?: number | string | null;
  km_reading?: number | string | null;
  service_intervals_completed?: ServiceCompletionRow[] | null;
  team_member?: string | null;
  every_fillup_check?: FillupTick[] | null;
  [column: string]: unknown;
};

export type EquipmentHomeRow = {
  id: string | number;
  name?: string | null; // feeds the notice label, sorted via localeCompare
  slug?: string | null;
  tracking_unit?: string | null;
  current_hours?: number | string | null;
  current_km?: number | string | null;
  service_intervals?: unknown;
  every_fillup_items?: unknown;
  warranty_expiration?: string | null;
  [column: string]: unknown;
};

export type Next30Event = {
  type: string;
  label: string;
  date: string;
  color: string;
  iconKey: string;
  reminder?: boolean;
  subline?: string;
};

export type MissedReport = {
  key: string;
  label?: string | null;
  iconKey: string;
  type: string;
  date: string;
};

export type EquipmentAttentionItem = {
  key: string;
  kind: string;
  type: string;
  typeLabel: string;
  detail: string;
  metaLabel?: string;
  pill?: string;
};

export type EquipmentAttentionNotice = {
  key: string;
  slug: string | null | undefined;
  label?: string | null;
  kind: string;
  type: string;
  detail: string;
  metaLabel: string | undefined;
  pill: string | undefined;
  items: EquipmentAttentionItem[];
  clearableKey: string | null;
};

export type FoldedEquipmentFuelings = {
  equipmentCompletions: Record<string, IntervalCompletion[]>;
  equipmentFuelings: Record<string, EquipmentFuelingRow[]>;
};

type FillupStreak = {
  label: string | number | null | undefined;
  streak: number;
};

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function asClearedSet(value: Set<unknown> | unknown[] | null | undefined): Set<unknown> {
  return value instanceof Set ? value : new Set(asArray(value));
}

function keySlug(value: unknown): string {
  return (
    String(value || 'unknown')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unknown'
  );
}

export function dailyTargetKey(
  prefix: string,
  row: {id?: string | number | null} | null | undefined,
  label: unknown,
  date: string,
): string {
  const id = row && row.id != null ? String(row.id).trim() : '';
  return `${id || `${prefix}-${keySlug(label)}`}|${date}`;
}

export function foldEquipmentFuelings(rows: EquipmentFuelingRow[] | null = []): FoldedEquipmentFuelings {
  const equipmentCompletions: Record<string, IntervalCompletion[]> = {};
  const equipmentFuelings: Record<string, EquipmentFuelingRow[]> = {};
  for (const r of asArray(rows)) {
    if (!equipmentFuelings[r.equipment_id]) equipmentFuelings[r.equipment_id] = [];
    equipmentFuelings[r.equipment_id].push(r);
    const comps = Array.isArray(r.service_intervals_completed) ? r.service_intervals_completed : [];
    if (comps.length > 0) {
      const fallbackReading =
        r.hours_reading != null ? Number(r.hours_reading) : r.km_reading != null ? Number(r.km_reading) : null;
      const normalized = comps.map((c) => ({
        ...c,
        reading_at_completion: c && c.reading_at_completion != null ? Number(c.reading_at_completion) : fallbackReading,
        team_member: c && c.team_member != null ? c.team_member : r.team_member || null,
      }));
      equipmentCompletions[r.equipment_id] = [...(equipmentCompletions[r.equipment_id] || []), ...normalized];
    }
  }
  for (const id in equipmentFuelings) {
    equipmentFuelings[id].sort((a, b) => {
      const ra = a.hours_reading != null ? Number(a.hours_reading) : a.km_reading != null ? Number(a.km_reading) : null;
      const rb = b.hours_reading != null ? Number(b.hours_reading) : b.km_reading != null ? Number(b.km_reading) : null;
      if (ra != null && rb != null && ra !== rb) return rb - ra;
      return String(b.date || '').localeCompare(String(a.date || ''));
    });
  }
  return {equipmentCompletions, equipmentFuelings};
}

export function buildNext30Events({
  batches,
  breedingCycles,
  farrowingRecs,
  feederGroups,
  today = new Date(),
}: {
  batches?: HomeBroilerBatch[] | null;
  breedingCycles?: PigBreedingCycle[] | null;
  farrowingRecs?: HomeFarrowingRecord[] | null;
  feederGroups?: PigFeederGroup[] | null;
  today?: DateInput;
} = {}): Next30Event[] {
  const todayDate = today instanceof Date ? today : new Date(today);
  const todayStr = toISO(todayDate);
  const in30 = toISO(addDays(todayDate, 30));
  const weekEvents: Next30Event[] = [];

  asArray(batches).forEach((b) => {
    const live = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
    if (!live) return;
    const brooderIn = resolveBrooderEntryDate(b.hatchDate, b.brooderIn);
    if (brooderIn >= todayStr && brooderIn <= in30) {
      weekEvents.push({
        type: 'brooder-in',
        label: `${b.name} enters brooder`,
        date: brooderIn,
        color: '#065f46',
        iconKey: ANIMAL_ICON_KEYS.broiler,
      });
    }
    if (live.schoonerIn >= todayStr && live.schoonerIn <= in30) {
      weekEvents.push({
        type: 'schooner-in',
        label: `${b.name} moves to schooner`,
        date: live.schoonerIn,
        color: '#a16207',
        iconKey: ANIMAL_ICON_KEYS.broiler,
      });
    }
    if (b.processingDate >= todayStr && b.processingDate <= in30) {
      weekEvents.push({
        type: 'processing',
        label: `${b.name} processing day`,
        date: b.processingDate,
        color: '#7f1d1d',
        iconKey: ANIMAL_ICON_KEYS.broiler,
      });
    }
    if (b.hatchDate) {
      const wk4date = toISO(addDays(new Date(b.hatchDate + 'T12:00:00'), 28));
      if (wk4date >= todayStr && wk4date <= in30 && !(parseFloat(b.week4Lbs) > 0)) {
        weekEvents.push({
          type: 'wt-4wk',
          label: `${b.name} — record 4-week weights`,
          date: wk4date,
          color: '#854d0e',
          iconKey: PLANNER_ICON_KEYS.weighins,
          reminder: true,
        });
      }
    }
    if (b.hatchDate) {
      const wk6date = toISO(addDays(new Date(b.hatchDate + 'T12:00:00'), 42));
      if (wk6date >= todayStr && wk6date <= in30 && !(parseFloat(b.week6Lbs) > 0)) {
        weekEvents.push({
          type: 'wt-6wk',
          label: `${b.name} — record 6-week weights`,
          date: wk6date,
          color: '#854d0e',
          iconKey: PLANNER_ICON_KEYS.weighins,
          reminder: true,
        });
      }
    }
  });

  const weekSeqMap = buildCycleSeqMap(asArray(breedingCycles));
  asArray(breedingCycles).forEach((c) => {
    const tl = calcBreedingTimeline(c.exposureStart);
    if (!tl) return;
    const lbl = cycleLabel(c, weekSeqMap);
    if (tl.farrowingStart >= todayStr && tl.farrowingStart <= in30) {
      weekEvents.push({
        type: 'farrow-open',
        label: `${lbl} farrowing window opens`,
        date: tl.farrowingStart,
        color: '#1e40af',
        iconKey: ANIMAL_ICON_KEYS.pig,
      });
    }
    if (tl.farrowingEnd >= todayStr && tl.farrowingEnd <= in30) {
      weekEvents.push({
        type: 'farrow-close',
        label: `${lbl} farrowing window closes`,
        date: tl.farrowingEnd,
        color: '#be185d',
        iconKey: ANIMAL_ICON_KEYS.pig,
      });
    }
    if (tl.farrowingStart <= in30 && tl.farrowingEnd >= todayStr) {
      const expected = [...(c.boar1Tags || '').split(/[\n,]+/), ...(c.boar2Tags || '').split(/[\n,]+/)]
        .map((t) => t.trim())
        .filter(Boolean);
      const farrowed = new Set(
        asArray(farrowingRecs)
          .filter((r) => r.group === c.group)
          .map((r) => r.sow.trim()),
      );
      const pending = expected.filter((t) => !farrowed.has(t));
      if (pending.length > 0) {
        const windowActive = tl.farrowingStart <= todayStr;
        weekEvents.push({
          type: 'farrow-due',
          label: `${lbl} sow group farrowing window ${windowActive ? 'active' : 'opens'}`,
          date: tl.farrowingStart,
          subline: windowActive
            ? `Window ${fmt(tl.farrowingStart)}-${fmt(tl.farrowingEnd)}`
            : `Opens ${fmt(tl.farrowingStart)}`,
          color: '#1e40af',
          iconKey: ANIMAL_ICON_KEYS.pig,
        });
      }
    }
  });

  asArray(feederGroups).forEach((g) => {
    const cycle = asArray(breedingCycles).find((c) => c.id === g.cycleId);
    if (!cycle) return;
    const tl = calcBreedingTimeline(cycle.exposureStart);
    if (!tl) return;
    const farrowMid = new Date(tl.farrowingStart + 'T12:00:00');
    const sixMonths = toISO(addDays(farrowMid, 183));
    if (sixMonths >= todayStr && sixMonths <= in30) {
      weekEvents.push({
        type: 'pig-age',
        label: `${g.batchName} hitting ~6 months`,
        date: sixMonths,
        color: '#92400e',
        iconKey: ANIMAL_ICON_KEYS.pig,
      });
    }
  });

  return weekEvents.sort((a, b) => a.date.localeCompare(b.date));
}

// ── Missing data (Ronnie's note 2, 2026-10-06) ──────────────────────────────
// Records that are missing a fact someone must enter. Shown on both Home
// surfaces above Missed Daily Reports, with its own rules: a row is never
// cleared by hand, it leaves when the data is entered (Ronnie, 2026-10-08).
//
// Rule 1: an on-farm broiler batch with no day-1 bird count (birdCountActual),
// from the day after it arrived (Ronnie, 2026-10-08). Until it is entered the
// on-farm counts use the ordered count, marked 'est.' (constitution Q50).

export type MissingDataRow = {
  key: string;
  kind: 'broiler-day1-count';
  batchName: string;
  arrivedDate: string;
  orderedBirds: number;
  iconKey: string;
  detail: string;
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'Sep 28', with the year added outside the current year (constitution §5).
export function fmtMonthDay(iso: string, todayIso: string): string {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  const base = SHORT_MONTHS[m - 1] + ' ' + d;
  return String(y) === String(todayIso).slice(0, 4) ? base : base + ', ' + y;
}

export function buildMissingData({
  batches,
  today = new Date(),
}: {
  batches?: HomeBroilerBatch[] | null;
  today?: DateInput;
} = {}): MissingDataRow[] {
  const todayStr = typeof today === 'string' ? today.slice(0, 10) : toISO(today);
  const rows: MissingDataRow[] = [];
  asArray(batches).forEach((b) => {
    if (!b || !b.name) return;
    if (calcPoultryStatus(b, todayStr) !== 'active') return;
    const arrived = String(b.brooderIn || b.hatchDate || '').slice(0, 10);
    if (!arrived || todayStr <= arrived) return;
    if (!broilerDayOneCountMissing(b)) return;
    const ordered = Math.max(0, parseInt(String(b.birdCount), 10) || 0);
    rows.push({
      key: `missing-day1-count|${b.name}`,
      kind: 'broiler-day1-count',
      batchName: b.name,
      arrivedDate: arrived,
      orderedBirds: ordered,
      iconKey: ANIMAL_ICON_KEYS.broiler,
      detail:
        'No day-1 bird count · arrived ' +
        fmtMonthDay(arrived, todayStr) +
        (ordered > 0 ? ' · counting ' + ordered.toLocaleString() + ' ordered' : ''),
    });
  });
  return rows.sort((a, b) => a.arrivedDate.localeCompare(b.arrivedDate) || a.batchName.localeCompare(b.batchName));
}

export function buildMissedDailyReports({
  batches,
  broilerDailys,
  pigDailys,
  layerDailysRecent,
  cattleDailysRecent,
  sheepDailysRecent,
  feederGroups,
  breeders,
  layerGroups,
  cattleForHome,
  sheepForHome,
  missedCleared,
  today = new Date(),
}: {
  batches?: HomeBroilerBatch[] | null;
  broilerDailys?: DailyReportRow[] | null;
  pigDailys?: DailyReportRow[] | null;
  layerDailysRecent?: DailyReportRow[] | null;
  cattleDailysRecent?: DailyReportRow[] | null;
  sheepDailysRecent?: DailyReportRow[] | null;
  feederGroups?: PigFeederGroup[] | null;
  breeders?: PigBreederRow[] | null;
  layerGroups?: LayerGroupRow[] | null;
  cattleForHome?: DailyReportRow[] | null;
  sheepForHome?: DailyReportRow[] | null;
  missedCleared?: Set<unknown> | unknown[] | null;
  today?: DateInput;
} = {}): MissedReport[] {
  const cleared = asClearedSet(missedCleared);
  const todayDate = today instanceof Date ? today : new Date(today);
  const allMissed: MissedReport[] = [];
  for (let daysBack = 1; daysBack <= 7; daysBack++) {
    const checkDate = toISO(addDays(todayDate, -daysBack));
    const broilerCheck = new Set(
      asArray(broilerDailys)
        .filter((d) => d.date === checkDate)
        .map((d) =>
          (d.batch_label || '')
            .toLowerCase()
            .trim()
            .replace(/^\(processed\)\s*/, ''),
        ),
    );
    const pigCheck = new Set(
      asArray(pigDailys)
        .filter((d) => d.date === checkDate)
        .map((d) => (d.batch_label || '').toLowerCase().trim()),
    );
    const layerCheck = new Set(
      asArray(layerDailysRecent)
        .filter((d) => d.date === checkDate)
        .map((d) => (d.batch_label || '').toLowerCase().trim()),
    );

    asArray(batches)
      .filter((b) => b.status === 'active')
      .forEach((b) => {
        const earliestDate = b.brooderIn || b.hatchDate;
        if (earliestDate && checkDate < earliestDate) return;
        if (b.processingDate && checkDate > b.processingDate) return;
        const key = dailyTargetKey('broiler', b, b.name, checkDate);
        if (!broilerCheck.has((b.name || '').toLowerCase().trim()) && !cleared.has(key)) {
          allMissed.push({key, label: b.name, iconKey: ANIMAL_ICON_KEYS.broiler, type: 'Broiler', date: checkDate});
        }
      });

    activePigFeederDailyTargets(asArray(feederGroups), {breeders: asArray(breeders)}).forEach((t) => {
      const key = dailyTargetKey('pig', t, t.name, checkDate);
      if (!pigCheck.has((t.name || '').toLowerCase().trim()) && !cleared.has(key)) {
        allMissed.push({
          key,
          label: t.name,
          iconKey: ANIMAL_ICON_KEYS.pig,
          type: `Pig · ${t.parentBatchName}`,
          date: checkDate,
        });
      }
    });

    const hasActiveSows = asArray(breeders).some((b) => !b.archived && (b.sex === 'Sow' || b.sex === 'Gilt'));
    const hasActiveBoars = asArray(breeders).some((b) => !b.archived && b.sex === 'Boar');
    if (hasActiveSows) {
      const key = `pig-stock-sows|${checkDate}`;
      const reportedSowGroups = new Set<SowGroupNumber>();
      for (const lbl of pigCheck) {
        const g = sowGroupOf(lbl);
        if (g !== null) reportedSowGroups.add(g);
      }
      const hasAnySowsReport = [...pigCheck].some(isSowsDailyLabel);
      if (!hasAnySowsReport && !cleared.has(key)) {
        allMissed.push({key, label: 'SOWS', iconKey: ANIMAL_ICON_KEYS.pig, type: 'Pig', date: checkDate});
      } else if (reportedSowGroups.size > 0 && reportedSowGroups.size < SOW_GROUP_NUMBERS.length) {
        // A sow group is missing on a day when others reported (P5).
        for (const n of SOW_GROUP_NUMBERS) {
          if (!reportedSowGroups.has(n)) {
            const groupKey = `pig-stock-sows-${n}|${checkDate}`;
            if (!cleared.has(groupKey)) {
              allMissed.push({
                key: groupKey,
                label: sowGroupLabel(n),
                iconKey: ANIMAL_ICON_KEYS.pig,
                type: 'Pig',
                date: checkDate,
              });
            }
          }
        }
      }
    }
    if (hasActiveBoars) {
      const key = `pig-stock-boars|${checkDate}`;
      if (!pigCheck.has('boars') && !cleared.has(key)) {
        allMissed.push({key, label: 'BOARS', iconKey: ANIMAL_ICON_KEYS.pig, type: 'Pig', date: checkDate});
      }
    }

    // The canonical ppp-layer-groups-v1 rows are the only status owner for a
    // layer daily target, and `filterActiveLayerGroups` is the same
    // `status === 'active'` test with two properties the inline filter did not
    // have: a legacy/absent/unknown status resolves to inactive rather than
    // slipping through some future comparison, and duplicate rows sharing one
    // name fail closed. A group switched off on /layer/groups therefore stops
    // being a missed-report target on the next render of BOTH Home surfaces.
    filterActiveLayerGroups(asArray(layerGroups)).forEach((g) => {
      const key = dailyTargetKey('layer', g, g.name, checkDate);
      if (!layerCheck.has((g.name || '').toLowerCase().trim()) && !cleared.has(key)) {
        allMissed.push({key, label: g.name, iconKey: ANIMAL_ICON_KEYS.layer, type: 'Layer', date: checkDate});
      }
    });

    const cattleCheck = new Set(
      asArray(cattleDailysRecent)
        .filter((d) => d.date === checkDate)
        .map((d) => d.herd),
    );
    ['mommas', 'backgrounders', 'finishers', 'bulls'].forEach((h) => {
      if (!asArray(cattleForHome).some((c) => c.herd === h)) return;
      const key = `cattle-${h}|${checkDate}`;
      if (!cattleCheck.has(h) && !cleared.has(key)) {
        allMissed.push({
          key,
          label: h.charAt(0).toUpperCase() + h.slice(1),
          iconKey: ANIMAL_ICON_KEYS.cattle,
          type: 'Cattle',
          date: checkDate,
        });
      }
    });

    const sheepCheck = new Set(
      asArray(sheepDailysRecent)
        .filter((d) => d.date === checkDate)
        .map((d) => d.flock),
    );
    ['rams', 'ewes', 'feeders'].forEach((f) => {
      if (!asArray(sheepForHome).some((s) => s.flock === f)) return;
      const key = `sheep-${f}|${checkDate}`;
      if (!sheepCheck.has(f) && !cleared.has(key)) {
        allMissed.push({
          key,
          label: f.charAt(0).toUpperCase() + f.slice(1),
          iconKey: ANIMAL_ICON_KEYS.sheep,
          type: 'Sheep',
          date: checkDate,
        });
      }
    });
  }
  return allMissed.sort((a, b) => b.date.localeCompare(a.date));
}

// Per-item ordering within a single equipment's grouped notice: overdue
// service intervals first, then every-fillup checklist streaks, then warranty.
// The `type` field on each item (service / checklist / warranty) is the
// operator-facing, visually-distinct category — set inline below — so a 50-hour
// checklist streak never reads like a duplicate of a service-interval alert.
const ATTENTION_KIND_ORDER: Record<string, number> = {overdue: 0, fillup_streak: 1, warranty: 2};

export function buildEquipmentAttention({
  equipment,
  equipmentFuelings,
  equipmentCompletions,
  missedCleared,
}: {
  equipment?: EquipmentHomeRow[] | null;
  equipmentFuelings?: Record<string, EquipmentFuelingRow[]> | null;
  equipmentCompletions?: Record<string, IntervalCompletion[]> | null;
  missedCleared?: Set<unknown> | unknown[] | null;
} = {}): EquipmentAttentionNotice[] {
  const cleared = asClearedSet(missedCleared);
  const equipmentAttention: EquipmentAttentionNotice[] = [];
  asArray(equipment).forEach((eq) => {
    const unit = eq.tracking_unit === 'km' ? 'km' : 'hours';
    const unitLabel = unit === 'km' ? 'km' : 'h';
    const currentReading = latestSaneReading(eq, (equipmentFuelings || {})[eq.id] || []);
    const intervals = Array.isArray(eq.service_intervals) ? eq.service_intervals : [];
    const completions = (equipmentCompletions || {})[eq.id] || [];

    // All attention items for THIS equipment accumulate here, then collapse into
    // a single grouped notice below so the same piece never emits multiple
    // duplicate-looking rows on the home dashboard / light portal.
    const items: EquipmentAttentionItem[] = [];

    if (Number.isFinite(currentReading) && currentReading > 0 && intervals.length > 0) {
      const statuses = computeIntervalStatus(intervals, completions, currentReading);
      // Number() is ToNumber — the exact coercion the `-` operator already
      // applied (equipment.ts's documented idiom); IntervalStatus types
      // hours_or_km as number | string | null straight from jsonb.
      const overdue = statuses.filter((s) => s.overdue).sort((a, b) => Number(a.hours_or_km) - Number(b.hours_or_km));
      for (const s of overdue) {
        const over = currentReading - s.next_due;
        const intervalLbl = s.label || s.hours_or_km + unitLabel + ' service';
        items.push({
          key: `equip-overdue-${eq.id}|${s.kind}|${s.hours_or_km}`,
          kind: 'overdue',
          type: 'service',
          typeLabel: 'Service',
          // detail stays the FULL string so single-text consumers (e.g.
          // LightHomePortal) keep the overdue quantity. HomeDashboard shows
          // metaLabel (service only) + the quantity in a pastel pill badge.
          detail: `${intervalLbl} · ${Math.round(over).toLocaleString()} ${unitLabel} overdue`,
          metaLabel: intervalLbl,
          pill: `${Math.round(over).toLocaleString()} ${unitLabel} overdue`,
        });
      }
    }

    const fillupItems = Array.isArray(eq.every_fillup_items) ? eq.every_fillup_items : [];
    const fuelings = (equipmentFuelings || {})[eq.id] || [];
    if (fillupItems.length > 0 && fuelings.length > 0) {
      const itemsWithStreak: FillupStreak[] = [];
      for (const item of fillupItems) {
        let streak = 0;
        for (const h of fuelings) {
          const ticks = Array.isArray(h.every_fillup_check) ? h.every_fillup_check : [];
          const wasTicked = ticks.some((t) => t && t.id === item.id);
          if (wasTicked) break;
          streak++;
        }
        if (streak > 0) itemsWithStreak.push({label: item.label || item.id, streak});
      }
      if (itemsWithStreak.length > 0) {
        const maxStreak = Math.max(...itemsWithStreak.map((i) => i.streak));
        const sample = itemsWithStreak
          .slice(0, 2)
          .map((i) => i.label)
          .join(', ');
        const more = itemsWithStreak.length > 2 ? ` +${itemsWithStreak.length - 2} more` : '';
        items.push({
          key: `equip-fillup-${eq.id}|streak${maxStreak}|n${itemsWithStreak.length}`,
          kind: 'fillup_streak',
          type: 'checklist',
          typeLabel: 'Checklist',
          // No metaLabel: checklist items fall back to the FULL detail on the
          // home dashboard (HomeDashboard renders it.metaLabel || it.detail), so
          // the max-streak count + sampled item labels stay visible. Only the
          // overdue-service item carries a truncated metaLabel (quantity -> pill).
          detail: `${itemsWithStreak.length} fillup item${itemsWithStreak.length === 1 ? '' : 's'} skipped (${maxStreak}× max streak): ${sample}${more}`,
          pill: `${itemsWithStreak.length} skipped`,
        });
      }
    }

    if (eq.warranty_expiration) {
      const d = daysSince(eq.warranty_expiration);
      if (d != null && d >= -WARRANTY_WINDOW_DAYS) {
        let detail;
        if (d > 0) detail = `Warranty expired ${d} day${d === 1 ? '' : 's'} ago`;
        else if (d === 0) detail = 'Warranty expires today';
        else detail = `Warranty expires in ${-d} day${-d === 1 ? '' : 's'}`;
        const key = `equip-warranty-${eq.id}|${eq.warranty_expiration}`;
        if (!cleared.has(key)) {
          items.push({
            key,
            kind: 'warranty',
            type: 'warranty',
            typeLabel: 'Warranty',
            detail,
            metaLabel: detail,
          });
        }
      }
    }

    if (items.length === 0) return;

    // Order items within the notice (service → checklist → warranty) and collapse
    // them into ONE grouped notice for this equipment. The primary (first) item
    // drives the notice-level kind/metaLabel/pill that HomeDashboard's LED color +
    // pastel badge already read; the notice `detail` joins every item's full text
    // so single-text consumers (LightHomePortal) still surface all due items with
    // their overdue quantities. `clearableKey` is the warranty item's key when
    // the ONLY attention on this piece is the (manually clearable) warranty.
    items.sort((a, b) => (ATTENTION_KIND_ORDER[a.kind] ?? 9) - (ATTENTION_KIND_ORDER[b.kind] ?? 9));
    const primary = items[0];
    const warrantyOnly = items.length === 1 && primary.kind === 'warranty';
    equipmentAttention.push({
      key: `equip-attention-${eq.id}`,
      slug: eq.slug,
      label: eq.name,
      kind: primary.kind,
      type: primary.type,
      detail: items.map((i) => i.detail).join(' · '),
      metaLabel: primary.metaLabel,
      pill: primary.pill,
      items,
      clearableKey: warrantyOnly ? primary.key : null,
    });
  });

  return equipmentAttention.sort((a, b) => {
    const ko = (ATTENTION_KIND_ORDER[a.kind] ?? 9) - (ATTENTION_KIND_ORDER[b.kind] ?? 9);
    if (ko !== 0) return ko;
    return String(a.label || '').localeCompare(String(b.label || ''));
  });
}
